import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Header from '@/components/ui/Header';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import DatePicker from '@/components/ui/DatePicker';
import Icon from '@/components/AppIcon';
import { useAuth } from '@/context/AuthContext';
import { useLocations } from '@/context/LocationsContext';
import useTranslation from '@/hooks/useTranslation';
import formatTranslation from '@/utils/formatTranslation';
import {
  fetchIncidentReports,
  createIncidentReport,
  updateIncidentReport,
  deleteIncidentReport,
} from '@/lib/api/incidentReports';
import IncidentFormModal from './components/IncidentFormModal';

const LANGUAGE_TO_LOCALE = {
  en: 'en-US',
  de: 'de-DE',
  fr: 'fr-FR',
};

const INCIDENT_STATUSES = {
  open: {
    labelKey: 'incidentReports.statuses.open',
    className: 'bg-warning/10 text-warning border border-warning/30',
  },
  investigating: {
    labelKey: 'incidentReports.statuses.investigating',
    className: 'bg-secondary/10 text-secondary border border-secondary/30',
  },
  resolved: {
    labelKey: 'incidentReports.statuses.resolved',
    className: 'bg-success/10 text-success border border-success/30',
  },
};

const INCIDENT_SEVERITIES = {
  high: {
    labelKey: 'incidentReports.severities.high',
    className: 'bg-error/10 text-error border border-error/30',
  },
  medium: {
    labelKey: 'incidentReports.severities.medium',
    className: 'bg-warning/10 text-warning border border-warning/30',
  },
  low: {
    labelKey: 'incidentReports.severities.low',
    className: 'bg-muted text-muted-foreground border border-border',
  },
};

const DEFAULT_INCIDENTS = [
  {
    id: 'INC-1042',
    title: 'Slip and fall in kitchen',
    location: 'Downtown Bistro',
    reportedBy: 'Jordan Smith',
    reportedAt: '2024-03-08T09:40:00Z',
    severity: 'high',
    status: 'investigating',
    summary:
      'Line cook slipped near fry station due to oil spill. First aid administered, no serious injury reported.',
    involvedEmployees: ['Jordan Smith', 'Avery Lopez', 'Shift Supervisor'],
    actionsTaken: 'Spill was contained and wet floor signage placed. First aid kit restocked.',
    followUp: 'Schedule refresher training on spill response and review housekeeping checklist.',
    impact: 'Minor ankle sprain reported. Kitchen line delayed 15 minutes for clean-up.',
    photos: [],
    isArchived: false,
    isDeleted: false,
  },
  {
    id: 'INC-1038',
    title: 'Customer allergic reaction',
    location: 'Riverside Cafe',
    reportedBy: 'Taylor Jones',
    reportedAt: '2024-03-05T19:15:00Z',
    severity: 'medium',
    status: 'resolved',
    summary:
      'Guest experienced mild reaction after ordering dessert. EMS called as precaution, guest declined transport.',
    involvedEmployees: ['Taylor Jones', 'Serving Team'],
    actionsTaken: 'EMS contacted, allergen protocols reviewed with server and kitchen.',
    followUp: 'Update dessert menu allergen labels and confirm guest follow-up call completed.',
    impact: 'Guest discomfort resolved on-site. No further medical attention required.',
    photos: [],
    isArchived: false,
    isDeleted: false,
  },
  {
    id: 'INC-1024',
    title: 'Equipment malfunction - Oven',
    location: 'Downtown Bistro',
    reportedBy: 'Alex Johnson',
    reportedAt: '2024-02-27T07:20:00Z',
    severity: 'low',
    status: 'open',
    summary:
      'Prep oven not maintaining temperature. Maintenance notified, shift adjusted to use backup equipment.',
    involvedEmployees: ['Alex Johnson', 'Maintenance Team'],
    actionsTaken: 'Maintenance ticket submitted and equipment taken offline.',
    followUp: 'Await technician visit and confirm calibration before returning to service.',
    impact: 'Prep timeline adjusted and backup oven utilized. No injuries.',
    photos: [],
    isArchived: false,
    isDeleted: false,
  },
  {
    id: 'INC-1019',
    title: 'Guest altercation',
    location: 'Seaside Grill',
    reportedBy: 'Casey Lee',
    reportedAt: '2024-02-21T21:05:00Z',
    severity: 'high',
    status: 'resolved',
    summary:
      'Two guests engaged in verbal dispute escalating to minor physical contact. Security intervened and local authorities notified.',
    involvedEmployees: ['Casey Lee', 'Security Staff'],
    actionsTaken: 'Security separated parties and statements collected for authorities.',
    followUp: 'File insurance report and review de-escalation procedures with front-of-house team.',
    impact: 'Short disruption to dining room. No staff injuries reported.',
    photos: [],
    isArchived: true,
    isDeleted: false,
  },
];

const normalizeComparableName = (value) => {
  if (typeof value !== 'string') {
    return '';
  }

  return value.trim().toLowerCase();
};

const withDefaultIncidentLabels = (labels = {}) => ({
  missingSummary: 'No incident details provided yet.',
  untitledIncident: 'Untitled incident',
  unassignedLocation: 'Unassigned location',
  unknownReporter: 'Unknown reporter',
  attachmentName: (index) => `Attachment ${index}`,
  unknownDate: 'Unknown date',
  ...labels,
});

const formatDate = (isoString, locale = 'en-US', fallback = 'Unknown date') => {
  if (!isoString) {
    return fallback;
  }

  const date = new Date(isoString);
  if (Number.isNaN(date.getTime())) {
    return fallback;
  }

  return date.toLocaleString(locale, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const truncateSummary = (text, fallback = 'No incident details provided yet.') => {
  if (!text) {
    return fallback;
  }

  const value = text.trim();
  if (!value) {
    return fallback;
  }

  if (value.length <= 220) {
    return value;
  }

  return `${value.slice(0, 217)}…`;
};

const normalizePhotos = (photos, labels = {}) => {
  if (!Array.isArray(photos)) {
    return [];
  }

  const { attachmentName } = withDefaultIncidentLabels(labels);

  return photos
    .filter(Boolean)
    .map((photo, index) => ({
      id: photo?.id ?? `photo-${index}`,
      fileName: photo?.fileName ?? attachmentName(index + 1),
      previewUrl: photo?.previewUrl ?? (typeof photo === 'string' ? photo : ''),
      persisted: photo?.persisted ?? false,
    }));
};

const normalizeIncident = (incident, labels = {}) => {
  if (!incident) {
    return null;
  }

  const resolvedLabels = withDefaultIncidentLabels(labels);
  const description = incident.description ?? incident.summary ?? '';

  return {
    id: incident.id ?? `INC-${Date.now()}`,
    title: incident.title ?? resolvedLabels.untitledIncident,
    location: incident.location ?? resolvedLabels.unassignedLocation,
    reportedBy: incident.reportedBy ?? resolvedLabels.unknownReporter,
    reportedAt: incident.reportedAt ?? new Date().toISOString(),
    severity: incident.severity ?? 'low',
    status: incident.status ?? 'open',
    summary: truncateSummary(incident.summary ?? description, resolvedLabels.missingSummary),
    description,
    involvedEmployees: Array.isArray(incident.involvedEmployees)
      ? incident.involvedEmployees.filter(Boolean)
      : [],
    actionsTaken: incident.actionsTaken ?? '',
    followUp: incident.followUp ?? '',
    impact: incident.impact ?? '',
    photos: normalizePhotos(incident.photos, resolvedLabels),
    isArchived: !!incident.isArchived,
    isDeleted: !!incident.isDeleted,
  };
};

const IncidentReports = () => {
  const { t, language } = useTranslation(['header', 'common', 'incidentReports']);
  const { user } = useAuth();
  const { locations, loading: locationsLoading } = useLocations();

  const localizedLabels = useMemo(
    () => ({
      missingSummary: t('incidentReports.defaults.missingSummary'),
      untitledIncident: t('incidentReports.defaults.untitledIncident'),
      unassignedLocation: t('incidentReports.defaults.unassignedLocation'),
      unknownReporter: t('incidentReports.defaults.unknownReporter'),
      attachmentName: (index) =>
        formatTranslation(t('incidentReports.defaults.attachmentName'), { index }),
      unknownDate: t('incidentReports.defaults.unknownDate'),
    }),
    [t],
  );

  const locale = LANGUAGE_TO_LOCALE[language] ?? LANGUAGE_TO_LOCALE.en ?? 'en-US';

  const [selectedLocation, setSelectedLocation] = useState(null);
  const fallbackIncidents = useMemo(
    () => DEFAULT_INCIDENTS.map((incident) => normalizeIncident(incident, localizedLabels)).filter(Boolean),
    [localizedLabels],
  );

  const [incidents, setIncidents] = useState([]);
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
  });
  const [modalState, setModalState] = useState({ isOpen: false, mode: 'create', incident: null });
  const [showArchived, setShowArchived] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [loadingIncidents, setLoadingIncidents] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const statusConfig = useMemo(
    () =>
      Object.entries(INCIDENT_STATUSES).reduce(
        (acc, [key, config]) => ({
          ...acc,
          [key]: { ...config, label: t(config.labelKey) },
        }),
        {},
      ),
    [t],
  );

  const severityConfig = useMemo(
    () =>
      Object.entries(INCIDENT_SEVERITIES).reduce(
        (acc, [key, config]) => ({
          ...acc,
          [key]: { ...config, label: t(config.labelKey) },
        }),
        {},
      ),
    [t],
  );

  const formatIncidentDate = useCallback(
    (isoString) => formatDate(isoString, locale, localizedLabels.unknownDate),
    [locale, localizedLabels],
  );

  const userRole = user?.role ?? 'employee';
  const normalizedRole = (userRole ?? '').toLowerCase();
  const canManageAllIncidents = useMemo(
    () => ['manager', 'admin', 'owner', 'supervisor'].includes(normalizedRole),
    [normalizedRole],
  );
  const userDisplayName = useMemo(() => {
    if (!user) {
      return '';
    }

    return (
      user?.fullName
      ?? user?.name
      ?? [user?.firstName, user?.lastName].filter(Boolean).join(' ')
      ?? user?.email
      ?? ''
    );
  }, [user]);

  const comparableUserName = useMemo(
    () => normalizeComparableName(userDisplayName),
    [userDisplayName],
  );

  const canModifyIncident = useCallback(
    (incident) => {
      if (canManageAllIncidents) {
        return true;
      }

      const incidentReporter = normalizeComparableName(incident?.reportedBy);
      return incidentReporter && comparableUserName && incidentReporter === comparableUserName;
    },
    [canManageAllIncidents, comparableUserName],
  );

  const resolvedLocationName = useMemo(() => {
    if (!selectedLocation) {
      return '';
    }

    if (typeof selectedLocation === 'string') {
      return selectedLocation;
    }

    return selectedLocation?.name ?? selectedLocation?.label ?? '';
  }, [selectedLocation]);

  const closeModal = useCallback(() => {
    setModalState({ isOpen: false, mode: 'create', incident: null });
  }, []);

  const openIncidentModal = useCallback((mode, incident = null) => {
    setModalState({ isOpen: true, mode, incident });
  }, []);

  useEffect(() => {
    if (!selectedLocation && Array.isArray(locations) && locations.length > 0) {
      setSelectedLocation(locations[0]);
    }
  }, [locations, selectedLocation]);

  useEffect(() => {
    let isActive = true;

    const loadIncidents = async () => {
      setLoadingIncidents(true);
      try {
        const response = await fetchIncidentReports();
        if (!isActive) {
          return;
        }
        const normalized = Array.isArray(response)
          ? response.map((incident) => normalizeIncident(incident, localizedLabels)).filter(Boolean)
          : [];
        setIncidents(normalized);
        setFetchError('');
      } catch (error) {
        console.error('Failed to load incident reports', error);
        if (!isActive) {
          return;
        }
        setIncidents(fallbackIncidents);
        setFetchError(t('incidentReports.page.loadError'));
      } finally {
        if (isActive) {
          setLoadingIncidents(false);
        }
      }
    };

    loadIncidents();

    return () => {
      isActive = false;
    };
  }, [fallbackIncidents, localizedLabels, t]);

  const filteredIncidents = useMemo(() => {
    const { startDate, endDate } = filters;
    const start = startDate ? new Date(startDate) : null;
    const end = endDate ? new Date(endDate) : null;

    return incidents
      .filter((incident) => {
        if (!incident?.reportedAt) {
          return true;
        }

        const reportedAt = new Date(incident.reportedAt);
        if (Number.isNaN(reportedAt.getTime())) {
          return true;
        }

        if (start && reportedAt < new Date(start.setHours(0, 0, 0, 0))) {
          return false;
        }

        if (end && reportedAt > new Date(end.setHours(23, 59, 59, 999))) {
          return false;
        }

        return true;
      })
      .sort((a, b) => new Date(b.reportedAt) - new Date(a.reportedAt));
  }, [filters, incidents]);

  const activeIncidents = useMemo(
    () => filteredIncidents.filter((incident) => !incident.isArchived && !incident.isDeleted),
    [filteredIncidents],
  );

  const archivedIncidents = useMemo(
    () => filteredIncidents.filter((incident) => incident.isArchived && !incident.isDeleted),
    [filteredIncidents],
  );

  const deletedIncidents = useMemo(
    () => filteredIncidents.filter((incident) => incident.isDeleted),
    [filteredIncidents],
  );

  const hasInactiveIncidents = useMemo(
    () => archivedIncidents.length > 0 || deletedIncidents.length > 0,
    [archivedIncidents, deletedIncidents],
  );

  const activeIncidentCount = useMemo(
    () =>
      incidents.filter(
        (incident) => !incident.isArchived && !incident.isDeleted && incident.status !== 'resolved',
      ).length,
    [incidents],
  );

  const highSeverityCount = useMemo(
    () => incidents.filter((incident) => !incident.isArchived && !incident.isDeleted && incident.severity === 'high').length,
    [incidents],
  );

  const handleFilterChange = (field) => (event) => {
    const value = event?.target?.value ?? '';
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const openCreateIncident = () => {
    openIncidentModal('create', {
      location: resolvedLocationName,
      reportedBy: userDisplayName,
    });
  };

  const openViewIncident = (incident) => {
    openIncidentModal('view', incident);
  };

  const openEditIncident = (incident) => {
    openIncidentModal('edit', incident);
  };

  const handleSubmitIncident = async (formPayload) => {
    if (modalState.mode === 'edit' && modalState.incident) {
      try {
        const updatedIncident = await updateIncidentReport(modalState.incident.id, formPayload);
        const normalizedIncident = normalizeIncident(updatedIncident, localizedLabels);
        setIncidents((prev) =>
          prev.map((incident) => (incident.id === modalState.incident.id ? normalizedIncident : incident)),
        );
      } catch (error) {
        console.error('Failed to update incident report', error);
        window.alert(
          t(
            'incidentReports.page.updateError',
            'We could not update the incident report. Please try again.',
          ),
        );
        return;
      }
    } else {
      try {
        const payload = {
          ...formPayload,
          reportedBy: formPayload.reportedBy ?? userDisplayName ?? localizedLabels.unknownReporter,
        };
        const createdIncident = await createIncidentReport(payload);
        const normalizedIncident = normalizeIncident(createdIncident, localizedLabels);
        setIncidents((prev) => [normalizedIncident, ...prev]);
      } catch (error) {
        console.error('Failed to create incident report', error);
        window.alert(
          t(
            'incidentReports.page.createError',
            'We could not save the incident report. Please try again.',
          ),
        );
        return;
      }
    }

    closeModal();
  };

  const handleArchiveToggle = async (incident) => {
    if (!incident) {
      return;
    }

    if (incident.isDeleted) {
      return;
    }

    const nextArchivedState = !incident.isArchived;
    setIncidents((prev) =>
      prev.map((entry) =>
        entry.id === incident.id
          ? { ...entry, isArchived: nextArchivedState }
          : entry,
      ),
    );

    if (nextArchivedState) {
      setShowArchived(true);
    }

    try {
      const updated = await updateIncidentReport(incident.id, { isArchived: nextArchivedState });
      const normalized = normalizeIncident(updated, localizedLabels);
      setIncidents((prev) =>
        prev.map((entry) => (entry.id === incident.id ? normalized : entry)),
      );
    } catch (error) {
      console.error('Failed to update incident archive state', error);
      setIncidents((prev) =>
        prev.map((entry) => (entry.id === incident.id ? incident : entry)),
      );
      window.alert(
        t(
          'incidentReports.page.updateError',
          'We could not update the incident report. Please try again.',
        ),
      );
    }
  };

  const handleDeleteIncident = async (incident) => {
    if (!incident) {
      return;
    }

    const confirmMessage = formatTranslation(t('incidentReports.actions.deleteConfirm'), {
      id: incident.id,
    });
    const confirmed = window.confirm(confirmMessage);
    if (!confirmed) {
      return;
    }

    const previousIncidents = [...incidents];
    setIncidents((prev) =>
      prev.map((entry) =>
        entry.id === incident.id ? { ...entry, isDeleted: true } : entry,
      ),
    );
    setShowDeleted(true);

    try {
      const updated = await deleteIncidentReport(incident.id);
      if (updated) {
        const normalized = normalizeIncident(updated, localizedLabels);
        setIncidents((prev) =>
          prev.map((entry) => (entry.id === incident.id ? normalized : entry)),
        );
      }
    } catch (error) {
      console.error('Failed to delete incident report', error);
      setIncidents(previousIncidents);
      window.alert(
        t('incidentReports.page.deleteError', 'We could not delete the incident report. Please try again.'),
      );
    }
  };

  const handleRestoreDeletedIncident = async (incident) => {
    if (!incident) {
      return;
    }

    const previousIncidents = [...incidents];
    setIncidents((prev) =>
      prev.map((entry) => (entry.id === incident.id ? { ...entry, isDeleted: false } : entry)),
    );

    try {
      const updated = await updateIncidentReport(incident.id, { isDeleted: false });
      const normalized = normalizeIncident(updated, localizedLabels);
      setIncidents((prev) => prev.map((entry) => (entry.id === incident.id ? normalized : entry)));
    } catch (error) {
      console.error('Failed to restore incident report', error);
      setIncidents(previousIncidents);
      window.alert(
        t('incidentReports.page.updateError', 'We could not update the incident report. Please try again.'),
      );
    }
  };

  const renderCollapsedSection = useCallback(
    ({ label, countLabel, isOpen, onToggle, children }) => (
      <div className="border border-border rounded-lg overflow-hidden">
        <button
          type="button"
          onClick={onToggle}
          className="w-full flex items-center justify-between px-4 py-3 bg-muted/50 hover:bg-muted transition-colors"
        >
          <div className="flex items-center gap-2 text-sm font-medium text-foreground">
            <Icon name={isOpen ? 'ChevronUp' : 'ChevronDown'} size={18} />
            {label}
          </div>
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {countLabel}
          </span>
        </button>
        {isOpen && <div className="p-4 space-y-4 border-t border-border bg-background/60">{children}</div>}
      </div>
    ),
    [],
  );

  const renderIncidentCard = (incident) => {
    if (!incident) {
      return null;
    }

    const severityDisplay = severityConfig[incident.severity] ?? severityConfig.low;
    const statusDisplay = statusConfig[incident.status] ?? statusConfig.open;
    const attachments = Array.isArray(incident.photos) ? incident.photos : [];
    const summaryText =
      incident.summary ?? truncateSummary(incident.description, localizedLabels.missingSummary);

    return (
      <div
        key={incident.id}
        className={`bg-muted/40 border border-border rounded-lg p-5 hover:border-primary/40 transition-colors ${
          incident.isArchived || incident.isDeleted ? 'opacity-90' : ''
        }`}
      >
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {incident.id}
              </span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${severityDisplay.className}`}
              >
                {formatTranslation(t('incidentReports.badges.severity'), {
                  severity: severityDisplay.label,
                })}
              </span>
              <span
                className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium ${statusDisplay.className}`}
              >
                {statusDisplay.label}
              </span>
              {incident.isArchived && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-muted text-muted-foreground border border-border/60">
                  <Icon name="Archive" size={14} className="mr-1" />
                  {t('incidentReports.badges.archived')}
                </span>
              )}
              {incident.isDeleted && (
                <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-destructive/10 text-destructive border border-destructive/30">
                  <Icon name="Trash2" size={14} className="mr-1" />
                  {t('incidentReports.badges.deleted')}
                </span>
              )}
            </div>

            <h3 className="text-lg font-semibold text-foreground">{incident.title}</h3>
            <p className="text-sm text-muted-foreground leading-relaxed">{summaryText}</p>

            {incident.impact ? (
              <p className="text-sm text-muted-foreground/90">
                <span className="font-medium text-foreground">{t('incidentReports.labels.impact')}</span>{' '}
                {incident.impact}
              </p>
            ) : null}

            {incident.actionsTaken ? (
              <p className="text-sm text-muted-foreground/90">
                <span className="font-medium text-foreground">{t('incidentReports.labels.actions')}</span>{' '}
                {incident.actionsTaken}
              </p>
            ) : null}

            {incident.followUp ? (
              <p className="text-sm text-muted-foreground/90">
                <span className="font-medium text-foreground">{t('incidentReports.labels.followUp')}</span>{' '}
                {incident.followUp}
              </p>
            ) : null}

            {incident.involvedEmployees?.length > 0 ? (
              <div className="space-y-2">
                <p className="text-xs uppercase text-muted-foreground tracking-wide">
                  {t('incidentReports.labels.involvedTeam')}
                </p>
                <div className="flex flex-wrap gap-2">
                  {incident.involvedEmployees.map((person) => (
                    <span
                      key={person}
                      className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs"
                    >
                      <Icon name="User" size={14} />
                      {person}
                    </span>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground/90">
              <div className="inline-flex items-center gap-2">
                <Icon name="MapPin" size={16} className="text-muted-foreground/70" />
                <span>{incident.location}</span>
              </div>
              <div className="inline-flex items-center gap-2">
                <Icon name="User" size={16} className="text-muted-foreground/70" />
                <span>
                  {formatTranslation(t('incidentReports.labels.reportedBy'), {
                    name: incident.reportedBy,
                  })}
                </span>
              </div>
              <div className="inline-flex items-center gap-2">
                <Icon name="CalendarClock" size={16} className="text-muted-foreground/70" />
                <span>{formatIncidentDate(incident.reportedAt)}</span>
              </div>
            </div>

            {attachments.length > 0 && (
              <div className="space-y-2 pt-2">
                <p className="text-xs uppercase text-muted-foreground tracking-wide">
                  {t('incidentReports.labels.photos')}
                </p>
                <div className="flex flex-wrap gap-3">
                  {attachments.map((photo) => (
                    <div key={photo.id} className="w-20">
                      <div className="w-20 h-20 rounded-md overflow-hidden border border-border bg-muted">
                        {photo.previewUrl ? (
                          <img
                            src={photo.previewUrl}
                            alt={photo.fileName ?? t('incidentReports.labels.photoAlt')}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center text-muted-foreground/70">
                            <Icon name="Image" size={20} />
                          </div>
                        )}
                      </div>
                      <p className="text-[10px] text-muted-foreground truncate mt-1">{photo.fileName}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="shrink-0 flex flex-col items-stretch gap-2 min-w-[220px]">
            <Button
              variant="ghost"
              className="justify-start"
              iconName="Eye"
              iconPosition="left"
              onClick={() => openViewIncident(incident)}
            >
              {t('incidentReports.actions.viewDetails')}
            </Button>
            {canModifyIncident(incident) && !incident.isDeleted && (
              <>
                <Button
                  variant="outline"
                  className="justify-start"
                  iconName="Pencil"
                  iconPosition="left"
                  onClick={() => openEditIncident(incident)}
                >
                  {t('incidentReports.actions.editReport')}
                </Button>
                <Button
                  variant="ghost"
                  className="justify-start"
                  iconName={incident.isArchived ? 'Undo2' : 'Archive'}
                  iconPosition="left"
                  onClick={() => handleArchiveToggle(incident)}
                >
                  {incident.isArchived
                    ? t('incidentReports.actions.restoreIncident')
                    : t('incidentReports.actions.archiveIncident')}
                </Button>
                <Button
                  variant="destructive"
                  className="justify-start"
                  iconName="Trash2"
                  iconPosition="left"
                  onClick={() => handleDeleteIncident(incident)}
                >
                  {t('incidentReports.actions.deleteReport')}
                </Button>
              </>
            )}
            {canModifyIncident(incident) && incident.isDeleted ? (
              <Button
                variant="outline"
                className="justify-start"
                iconName="Undo2"
                iconPosition="left"
                onClick={() => handleRestoreDeletedIncident(incident)}
              >
                {t('incidentReports.actions.restoreDeleted')}
              </Button>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background">
      <Header
        userRole={userRole}
        selectedLocation={
          resolvedLocationName || (locationsLoading ? t('header.location.loadingLocations') : '')
        }
        locations={locations}
        onLocationChange={setSelectedLocation}
        allowLocationSelection
        notifications={{ messages: 0, timeOff: 0 }}
      />
      <main className="pt-16">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-foreground">{t('incidentReports.page.title')}</h1>
              <p className="text-muted-foreground mt-2 max-w-2xl">{t('incidentReports.page.description')}</p>
            </div>
            <Button variant="default" iconName="Plus" onClick={openCreateIncident}>
              {t('incidentReports.page.cta')}
            </Button>
          </div>

          {fetchError ? (
            <div className="rounded-lg border border-warning/40 bg-warning/10 px-4 py-3 text-sm text-warning">
              {fetchError}
            </div>
          ) : null}

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase text-muted-foreground tracking-wide">
                    {t('incidentReports.page.stats.active')}
                  </p>
                  <p className="text-2xl font-semibold text-foreground mt-2">{activeIncidentCount}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-warning/10 text-warning flex items-center justify-center">
                  <Icon name="AlertTriangle" size={20} />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase text-muted-foreground tracking-wide">
                    {t('incidentReports.page.stats.highSeverity')}
                  </p>
                  <p className="text-2xl font-semibold text-foreground mt-2">{highSeverityCount}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-error/10 text-error flex items-center justify-center">
                  <Icon name="ShieldAlert" size={20} />
                </div>
              </div>
            </div>

            <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase text-muted-foreground tracking-wide">
                    {t('incidentReports.page.stats.total')}
                  </p>
                  <p className="text-2xl font-semibold text-foreground mt-2">{incidents.length}</p>
                </div>
                <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                  <Icon name="ClipboardList" size={20} />
                </div>
              </div>
            </div>
          </div>

          <div className="bg-card border border-border rounded-xl shadow-elevation-1 p-6 space-y-6">
            <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-foreground">{t('incidentReports.activity.title')}</h2>
                <p className="text-sm text-muted-foreground">{t('incidentReports.activity.description')}</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full lg:w-auto">
                <DatePicker
                  label={t('incidentReports.activity.filters.startDate')}
                  value={filters.startDate}
                  onChange={handleFilterChange('startDate')}
                />
                <DatePicker
                  label={t('incidentReports.activity.filters.endDate')}
                  value={filters.endDate}
                  onChange={handleFilterChange('endDate')}
                  min={filters.startDate}
                />
              </div>
            </div>

            <div className="space-y-4">
              {loadingIncidents ? (
                <div className="py-10 text-center text-muted-foreground">
                  {t('incidentReports.page.loading')}
                </div>
              ) : activeIncidents.length === 0 && !hasInactiveIncidents ? (
                <div className="border border-dashed border-border rounded-xl p-8 text-center text-muted-foreground">
                  <Icon name="Inbox" size={32} className="mx-auto mb-3 text-muted-foreground/70" />
                  <p className="text-sm">
                    {t('incidentReports.activity.empty.all')}
                  </p>
                </div>
              ) : (
                <>
                  {activeIncidents.length > 0 ? (
                    activeIncidents.map((incident) => renderIncidentCard(incident))
                  ) : (
                    <p className="text-sm text-muted-foreground italic">
                      {t('incidentReports.activity.empty.inactiveOnly')}
                    </p>
                  )}

                  {archivedIncidents.length > 0
                    ? renderCollapsedSection({
                        label: t('incidentReports.activity.archived.toggle'),
                        countLabel: formatTranslation(t('incidentReports.activity.archived.count'), {
                          count: archivedIncidents.length,
                        }),
                        isOpen: showArchived,
                        onToggle: () => setShowArchived((prev) => !prev),
                        children: archivedIncidents.map((incident) => renderIncidentCard(incident)),
                      })
                    : null}

                  {deletedIncidents.length > 0
                    ? renderCollapsedSection({
                        label: t('incidentReports.activity.deleted.toggle'),
                        countLabel: formatTranslation(t('incidentReports.activity.deleted.count'), {
                          count: deletedIncidents.length,
                        }),
                        isOpen: showDeleted,
                        onToggle: () => setShowDeleted((prev) => !prev),
                        children: deletedIncidents.map((incident) => renderIncidentCard(incident)),
                      })
                    : null}
                </>
              )}
            </div>
          </div>
        </div>
      </main>
      <IncidentFormModal
        isOpen={modalState.isOpen}
        mode={modalState.mode}
        initialIncident={modalState.incident}
        onSubmit={handleSubmitIncident}
        onClose={closeModal}
        locations={locations}
        currentUser={user}
      />
    </div>
  );
};

export default IncidentReports;
